import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useUser } from "../../hooks/useAuth";
import { useMyLessons, useScheduleNotifications } from "../../hooks/useLessons";
import { useAuthStore } from "../../store/authStore";

/**
 * Current calendar month (1-12). Lessons unlock by absolute calendar month
 * (the month chosen per lesson), not relative to the student's intake date.
 * The classStartDate argument is kept for call-site compatibility but unused.
 */
function getCurrentProgramMonth(_classStartDate?: string | null): number {
  return new Date().getMonth() + 1;
}

/**
 * Helper to get lesson display title (Introduction for order 0)
 */
function getLessonTitle(lesson: { order: number; topic: string }): string {
  if (lesson.order === 0) {
    return `Introduction: ${lesson.topic}`;
  }
  return `Lesson ${lesson.order}: ${lesson.topic}`;
}

/**
 * Helper to build view URL for the resource viewer page
 */
function buildViewUrl(params: {
  url: string;
  title: string;
  type: "files" | "homework";
  level?: string;
  lesson?: number;
}): string {
  const searchParams = new URLSearchParams({
    url: params.url,
    title: params.title,
    type: params.type,
  });
  if (params.level) searchParams.set("level", params.level);
  if (params.lesson) searchParams.set("lesson", String(params.lesson));
  return `/lessons/view?${searchParams.toString()}`;
}

/**
 * Helper to format schedule display (handles both legacy and new formats)
 */
function formatScheduleDisplay(schedule: any): string {
  if (!schedule) return "Not set";

  const dayMap: Record<string, string> = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };

  // New format with different times per day (scheduleItems)
  if (schedule.scheduleItems && Array.isArray(schedule.scheduleItems)) {
    if (schedule.scheduleItems.length === 0) return "Not set";

    // Format with line breaks: "Tuesdays at 11:00\nThursdays at 08:00"
    return schedule.scheduleItems
      .map((item: any) => {
        const dayRaw = (item.day || item.dayFull || "").toLowerCase();
        const time = item.time || "";

        if (!dayRaw) return "";

        // Convert day to full name and make plural
        const fullDay = dayMap[dayRaw] || dayRaw;
        const pluralDay = fullDay.endsWith("s") ? fullDay : `${fullDay}s`;

        return time ? `${pluralDay} at ${time}` : pluralDay;
      })
      .filter(Boolean)
      .join("\n");
  }

  // Legacy format with same time for all days
  if (schedule.days && Array.isArray(schedule.days) && schedule.days.length > 0) {
    const time = schedule.time || "";
    if (time) {
      return `${schedule.days.join(" & ")} at ${time}`;
    }
    return schedule.days.join(" & ");
  }

  // Fallback: check if daysFull exists
  if (schedule.daysFull && Array.isArray(schedule.daysFull) && schedule.daysFull.length > 0) {
    const formattedDays = schedule.daysFull.map((d: string) => dayMap[d.toLowerCase()] || d);
    const time = schedule.time || "";
    if (time) {
      return `${formattedDays.join(" & ")} at ${time}`;
    }
    return formattedDays.join(" & ");
  }

  return "Not set";
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();

  // Fetch fresh user data from backend (includes updated class links)
  const { data: backendUser, isLoading: isUserLoading } = useUser();

  // Sync backend user data with Zustand store when it changes
  useEffect(() => {
    if (backendUser) {
      setUser(backendUser);
    }
  }, [backendUser, setUser]);

  // Use backendUser (fresh from API) if available, otherwise fallback to Zustand user
  const currentUser = backendUser || user;
  const firstName =
    currentUser?.firstName || currentUser?.fullName?.split(" ")[0];

  // Fetch real lessons data
  const { data: lessonsData, isLoading: isLessonsLoading } = useMyLessons();

  // Fetch schedule notifications
  const { data: notificationsData } = useScheduleNotifications();
  const hasScheduleChanges = (notificationsData?.notifications?.length ?? 0) > 0;

  // Combined loading state - wait for both user and lessons
  const isLoading = isUserLoading || isLessonsLoading;

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const whatsappNumber = "558197160139";
  const whatsappMessage = encodeURIComponent(
    "Hi! I need to reschedule my class.",
  );

  // Calculate current program month based on class start date
  const currentProgramMonth = useMemo(
    () => getCurrentProgramMonth(currentUser?.classInfo?.startDate),
    [currentUser?.classInfo?.startDate]
  );

  // Get current lesson based on the current program month
  const currentLesson = useMemo(() => {
    if (!lessonsData?.lessons) return null;

    // Filter lessons that are available (monthAvailable <= current month)
    const availableLessons = lessonsData.lessons.filter(
      (lesson) => lesson.monthAvailable <= currentProgramMonth
    );

    // If no available lessons, return null
    if (availableLessons.length === 0) return null;

    // Get lessons from the current month
    const currentMonthLessons = availableLessons.filter(
      (lesson) => lesson.monthAvailable === currentProgramMonth
    );

    // Prefer first lesson from current month with files
    const currentMonthWithFiles = currentMonthLessons.find((l) => l.filesUrl);
    if (currentMonthWithFiles) return currentMonthWithFiles;

    // Otherwise, return first lesson from current month
    if (currentMonthLessons.length > 0) return currentMonthLessons[0];

    // Fallback: return last available lesson from previous months
    return availableLessons[availableLessons.length - 1];
  }, [lessonsData?.lessons, currentProgramMonth]);

  const level = lessonsData?.level;
  const levelResources = lessonsData?.levelResources;

  // Get available lessons (only those that should be unlocked based on current month)
  const recentLessons = useMemo(() => {
    if (!lessonsData?.lessons) return [];

    // Filter lessons that are available (monthAvailable <= current month)
    const availableLessons = lessonsData.lessons.filter(
      (lesson) => lesson.monthAvailable <= currentProgramMonth
    );

    // Return first 3 available lessons
    return availableLessons.slice(0, 3);
  }, [lessonsData?.lessons, currentProgramMonth]);

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Top Section: Greeting + Quick Actions */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="space-y-1 sm:space-y-2">
            <p className="text-earth-700 text-sm">
              {getGreeting()}! Welcome back,
            </p>
            <h1 className="text-2xl sm:text-4xl font-display font-bold text-earth-800">
              {firstName || "Student"}
            </h1>
          </div>

          {/* Mini Quick Actions */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            <Link
              to="/lessons"
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white border border-earth-200 hover:border-earth-300 active:bg-earth-50 rounded-full text-sm text-earth-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line text-base"></i>
              <span>Lessons</span>
            </Link>
            <Link
              to="/financial"
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white border border-earth-200 hover:border-earth-300 active:bg-earth-50 rounded-full text-sm text-earth-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-wallet-3-line text-base"></i>
              <span>Financial</span>
            </Link>
            <Link
              to="/profile"
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white border border-earth-200 hover:border-earth-300 active:bg-earth-50 rounded-full text-sm text-earth-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-line text-base"></i>
              <span>Profile</span>
            </Link>
          </div>
        </div>

        {/* Schedule Change Alert Banner */}
        {hasScheduleChanges && (
          <Link
            to="/lessons"
            className="flex items-center gap-3 p-4 mb-6 bg-amber-50 border border-amber-200 rounded-2xl hover:bg-amber-100 transition-colors group"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
              <i className="ri-calendar-event-line text-amber-600 text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-amber-800">
                Schedule Update
              </p>
              <p className="text-sm text-amber-600">
                Your class schedule has changed. Tap to view details.
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-amber-500 text-xl group-hover:text-amber-700"></i>
          </Link>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        )}

        {/* Main Grid Layout */}
        {!isLoading && lessonsData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Column - Current Module (spans 2 cols) */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-earth-200 overflow-hidden">
              {/* Module Header */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-earth-50 to-sand-50 border-b border-earth-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-olive-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm font-medium text-earth-700">
                      Your current module
                    </span>
                  </div>
                  <div className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-white rounded-full text-xs font-medium text-earth-600 shadow-sm">
                    <i className="ri-graduation-cap-line"></i>
                    <span className="hidden xs:inline">
                      {lessonsData.lessons.length} lessons
                    </span>
                    <span className="xs:hidden">
                      {lessonsData.lessons.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Module Content */}
              <div className="p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:gap-5">
                  {/* Module Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <span className="px-2 sm:px-2.5 py-1 bg-olive-100 text-olive-700 rounded-lg text-xs font-semibold">
                        {level?.code}
                      </span>
                      <span className="text-earth-400">•</span>
                      <span className="text-earth-700 text-xs sm:text-sm truncate">
                        {level?.name}
                      </span>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-display font-bold text-earth-800 mb-1.5 sm:mb-2">
                      {currentLesson?.topic || "Your Next Class"}
                    </h2>
                    <p className="text-earth-700 text-sm sm:text-base mb-4 sm:mb-5 whitespace-pre-line">
                      {currentLesson?.grammarFocus
                        ? `Focus: ${currentLesson.grammarFocus}`
                        : currentUser?.classInfo?.schedule
                          ? formatScheduleDisplay(currentUser.classInfo.schedule)
                          : level?.description ||
                            "Continue learning English with Beyond Class"}
                    </p>

                    {/* Join Class Button - prioritize class-specific meet link */}
                    {currentUser?.classInfo?.meetLink ||
                    levelResources?.meet?.url ? (
                      <a
                        href={
                          currentUser?.classInfo?.meetLink ??
                          levelResources?.meet?.url ??
                          "#"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white font-medium rounded-xl transition-colors text-sm sm:text-base"
                      >
                        <i className="ri-video-on-line"></i>
                        Join class
                      </a>
                    ) : (
                      <Link
                        to="/lessons"
                        className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white font-medium rounded-xl transition-colors text-sm sm:text-base"
                      >
                        <i className="ri-book-open-line"></i>
                        View lessons
                      </Link>
                    )}
                  </div>

                  {/* Module Resources - Horizontal on mobile, Vertical on tablet+ */}
                  <div className="flex flex-col gap-2 pt-3 sm:pt-0 border-t sm:border-t-0 border-earth-100">
                    <p className="text-xs text-earth-700 uppercase tracking-wide font-medium">
                      Resources
                    </p>
                    <div className="grid grid-cols-3 sm:flex sm:flex-row gap-2">
                      {/* Current Lesson Files */}
                      {currentLesson?.filesUrl ? (
                        <button
                          onClick={() =>
                            navigate(
                              buildViewUrl({
                                url: currentLesson.filesUrl!,
                                title: getLessonTitle(currentLesson),
                                type: "files",
                                level: level?.code,
                                lesson: currentLesson.order,
                              }),
                            )
                          }
                          className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50 hover:bg-earth-100 active:bg-earth-200 rounded-xl text-xs sm:text-sm text-earth-700 transition-colors"
                        >
                          <i className="ri-file-text-line text-earth-400 text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Files
                          </span>
                        </button>
                      ) : (
                        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50/50 rounded-xl text-xs sm:text-sm text-earth-400">
                          <i className="ri-file-text-line text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Files
                          </span>
                        </div>
                      )}
                      {/* Whiteboard - Opens externally (Canva doesn't allow iframe) */}
                      {levelResources?.whiteboard?.url ? (
                        <a
                          href={levelResources.whiteboard.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50 hover:bg-earth-100 active:bg-earth-200 rounded-xl text-xs sm:text-sm text-earth-700 transition-colors"
                        >
                          <i className="ri-pencil-ruler-2-line text-earth-400 text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Board
                          </span>
                        </a>
                      ) : (
                        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50/50 rounded-xl text-xs sm:text-sm text-earth-400">
                          <i className="ri-pencil-ruler-2-line text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Board
                          </span>
                        </div>
                      )}

                      {/* Homework */}
                      {levelResources?.homework?.url ? (
                        <button
                          onClick={() =>
                            navigate(
                              buildViewUrl({
                                url: levelResources.homework!.url!,
                                title:
                                  levelResources.homework!.title ||
                                  `${level?.code} Homework`,
                                type: "homework",
                                level: level?.code,
                              }),
                            )
                          }
                          className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50 hover:bg-earth-100 active:bg-earth-200 rounded-xl text-xs sm:text-sm text-earth-700 transition-colors"
                        >
                          <i className="ri-todo-line text-earth-400 text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Homework
                          </span>
                        </button>
                      ) : (
                        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2.5 bg-earth-50/50 rounded-xl text-xs sm:text-sm text-earth-400">
                          <i className="ri-todo-line text-lg sm:text-base"></i>
                          <span className="text-center leading-tight">
                            Homework
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Stacked Cards */}
            <div className="flex flex-col gap-4">
              {/* Class Info Card */}
              {currentUser?.classInfo && (
                <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
                  <div className="px-4 py-2.5 sm:py-3 border-b border-earth-100 flex items-center justify-between">
                    <span className="font-semibold text-earth-800 text-sm">
                      Your class
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-olive-100 text-olive-700 rounded-full font-medium capitalize">
                      {currentUser.classInfo.classType}
                    </span>
                  </div>
                  <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                    {/* Schedule */}
                    <div className="flex items-start gap-2.5 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                        <i className="ri-calendar-line text-blue-600 text-xs sm:text-sm"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-earth-700">Schedule</p>
                        <p className="text-xs sm:text-sm font-medium text-earth-800 whitespace-pre-line">
                          {formatScheduleDisplay(currentUser.classInfo.schedule)}
                        </p>
                      </div>
                    </div>
                    {/* Teacher */}
                    {currentUser.classInfo.teacherName && (
                      <div className="flex items-start gap-2.5 sm:gap-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                          <i className="ri-user-star-line text-purple-600 text-xs sm:text-sm"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-earth-700">Teacher</p>
                          <p className="text-xs sm:text-sm font-medium text-earth-800 truncate">
                            {currentUser.classInfo.teacherName}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Meet Link */}
                    {currentUser.classInfo.meetLink && (
                      <a
                        href={currentUser.classInfo.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 w-full px-3 py-2.5 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white rounded-xl text-xs sm:text-sm font-medium transition-colors justify-center"
                      >
                        <i className="ri-video-on-line"></i>
                        Join Google Meet
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* WhatsApp CTA - Only for Individual Classes */}
              {currentUser?.classInfo?.classType === "individual" && (
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 hover:border-green-300 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                    <i className="ri-whatsapp-line text-white text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-earth-800 text-sm">
                      Reschedule class?
                    </p>
                    <p className="text-xs text-earth-700 truncate">
                      Chat on WhatsApp
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-green-500 group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}

              {/* Lessons List - Compact */}
              <div className="flex-1 bg-white rounded-2xl border border-earth-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-earth-100 flex items-center justify-between">
                  <span className="font-semibold text-earth-800 text-sm">
                    Module lessons
                  </span>
                  <Link
                    to="/lessons"
                    className="text-xs text-olive-600 hover:text-olive-700 font-medium"
                  >
                    View all →
                  </Link>
                </div>
                <div className="divide-y divide-earth-50">
                  {recentLessons.length > 0 ? (
                    recentLessons.map((lesson) => (
                      <div
                        key={lesson.id}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <div
                          className={`${lesson.order === 0 ? "w-12" : "w-8"} h-8 rounded-lg ${lesson.order === 0 ? "bg-emerald-100" : "bg-olive-100"} flex items-center justify-center ${lesson.order === 0 ? "text-emerald-600" : "text-olive-600"} text-xs font-bold`}
                        >
                          {lesson.order === 0
                            ? "Intro"
                            : String(lesson.order).padStart(2, "0")}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-earth-800 truncate">
                            {lesson.topic}
                          </p>
                          <p className="text-xs text-earth-700">
                            {lesson.grammarFocus ||
                              `Month ${lesson.monthAvailable}`}
                          </p>
                        </div>
                        {lesson.filesUrl && (
                          <button
                            onClick={() =>
                              navigate(
                                buildViewUrl({
                                  url: lesson.filesUrl!,
                                  title: getLessonTitle(lesson),
                                  type: "files",
                                  level: level?.code,
                                  lesson: lesson.order,
                                }),
                              )
                            }
                            className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                          >
                            <i className="ri-file-text-line text-earth-400"></i>
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-earth-700 text-sm">
                      No lessons available yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !lessonsData && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-open-line text-2xl text-earth-400"></i>
            </div>
            <p className="text-earth-600 font-medium">No module assigned yet</p>
            <p className="text-earth-700 text-sm mt-1">
              Contact your teacher to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
